'use client';

import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import CalendarView from '../habor-management/components/CalendarView';
import DayEventsModal from '../habor-management/components/DayEventsModal';
import FilterSection from '../habor-management/components/FilterSection';
import {
  CalendarEvent,
  ViewType,
  AssetType,
  statusConfig,
} from '../habor-management/components/types';
import {
  useLazyGetInspectorAssignmentsQuery,
  useGetAllClubBookingsQuery,
  useCompleteInspectionMutation,
  useAssignInspectorMutation,
} from '@/services/ClubBookingService';
import {
  useGetAvailableInspectorsQuery,
  type Inspector,
} from '@/services/ClubResourceService';
import { useGetAllMembersQuery } from '@/services/ClubService';
import type {
  MemberResponse,
  PaginatedApiResponse,
} from '@/types/club-management.types';
import {
  ClubBookingStatus,
  ResourceType,
} from '@/types/club-booking-management.types';
import { PriceUnit } from '@/types/club-resource.types';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDateWithOptions, formatDisplayDate } from '@/utils/Date';

const QUERY_LIMIT = 1000;
const POLLING_INTERVAL_MS = 30000;

interface InspectorEvent extends CalendarEvent {
  // booking linkage (if booked)
  bookingId?: string;
  itemId?: string;
  quantity?: number;
  // display helpers
  inspector?: { id: string; name: string | null; email: string };
  slotTime?: { start: string; end: string }; // HH:mm strings
}

const toLocalMidnight = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate());

const parseYmdToLocal = (ymd: string) => {
  const parts = ymd.split('-').map((p) => Number(p));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [year, month, day] = parts;
  return new Date(year, month - 1, day);
};

const isConfirmedFutureSlot = (ev: InspectorEvent) => {
  if (ev.status !== 'confirmed') return false;
  const dateOnly = parseYmdToLocal(ev.start);
  if (!dateOnly) return false;

  const [hh, mm] = (ev.slotTime?.start || '00:00')
    .split(':')
    .map((p) => Number(p));
  const slotDateTime = new Date(
    dateOnly.getFullYear(),
    dateOnly.getMonth(),
    dateOnly.getDate(),
    Number.isFinite(hh) ? hh : 0,
    Number.isFinite(mm) ? mm : 0
  );

  return slotDateTime.getTime() > Date.now();
};

const toUtcHM = (iso: string) => {
  const d = new Date(iso);
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

export default function InspectorSlotCalendar() {
  const { id: clubId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();

  const canReassignInspector = useMemo(
    () => hasPermission('ais_inspector_reassignment'),
    [hasPermission]
  );

  const canReadClubMembers = useMemo(
    () => hasPermission('read_club_members'),
    [hasPermission]
  );

  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<ViewType>('week');
  const [statusFilters, setStatusFilters] = useState<Set<string>>(
    new Set(['available', 'confirmed', 'completed', 'cancelled'])
  );
  const [userFilter, setUserFilter] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [memberManuallySelected, setMemberManuallySelected] = useState(false);
  const [selectedResource, setSelectedResource] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<InspectorEvent | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInspectorUserId, setSelectedInspectorUserId] = useState('');
  const [showAllOutOfRangeMonths, setShowAllOutOfRangeMonths] = useState(false);
  const [showAllOutOfRangeDates, setShowAllOutOfRangeDates] = useState(false);
  const [dayEventsModal, setDayEventsModal] = useState<{
    open: boolean;
    date?: Date;
    events?: CalendarEvent[];
  }>({ open: false });
  const [isCompleting, setIsCompleting] = useState(false);

  // Refs for modal accessibility
  const modalRef = useRef<HTMLDialogElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const [
    triggerAssignments,
    { data: assignmentsRes, isLoading: isAssignmentsLoading },
  ] = useLazyGetInspectorAssignmentsQuery();

  // Trigger assignments fetch explicitly so we never send stale member_id on refresh
  useEffect(() => {
    if (!clubId) return;
    const args: { club_id: string; member_id?: string } = { club_id: clubId };
    if (memberManuallySelected && selectedMemberId)
      args.member_id = selectedMemberId;
    // initial fetch
    triggerAssignments(args);
    // polling
    const id = setInterval(() => {
      triggerAssignments(args);
    }, POLLING_INTERVAL_MS);
    return () => clearInterval(id);
  }, [clubId, selectedMemberId, memberManuallySelected, triggerAssignments]);

  // Handle Escape key to close modal (WCAG)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isModalOpen) {
        setIsModalOpen(false);
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen]);

  // Focus management and focus trap (WCAG)
  useEffect(() => {
    if (isModalOpen && closeButtonRef.current) {
      // Auto-focus the close button when modal opens
      closeButtonRef.current.focus();

      // Focus trap: keep focus within modal
      const handleFocusTrap = (e: KeyboardEvent) => {
        if (e.key === 'Tab' && modalRef.current) {
          const focusableElements = modalRef.current.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          const firstElement = focusableElements[0] as HTMLElement;
          const lastElement = focusableElements[
            focusableElements.length - 1
          ] as HTMLElement;

          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      };

      document.addEventListener('keydown', handleFocusTrap);

      return () => {
        document.removeEventListener('keydown', handleFocusTrap);
      };
    }
  }, [isModalOpen]);

  const { data: bookingsRes, isLoading: isBookingsLoading } =
    useGetAllClubBookingsQuery(
      {
        club_id: clubId || undefined,
        page: 1,
        limit: QUERY_LIMIT,
      },
      {
        skip: !clubId,
        pollingInterval: POLLING_INTERVAL_MS,
        refetchOnMountOrArgChange: true,
        refetchOnFocus: true,
        refetchOnReconnect: true,
      }
    );

  const [completeInspection] = useCompleteInspectionMutation();
  const [assignInspector, { isLoading: isAssigningInspector }] =
    useAssignInspectorMutation();

  const availableInspectorsQueryParams = useMemo(() => {
    if (!clubId) return null;
    if (!selectedEvent) return null;
    if (!selectedEvent.slotTime?.start || !selectedEvent.slotTime?.end)
      return null;

    return {
      club_id: clubId,
      date: selectedEvent.start,
      start_time: selectedEvent.slotTime.start,
      end_time: selectedEvent.slotTime.end,
    };
  }, [clubId, selectedEvent]);

  const { data: availableInspectors = [], isFetching: isFetchingInspectors } =
    useGetAvailableInspectorsQuery(availableInspectorsQueryParams || {}, {
      skip:
        !availableInspectorsQueryParams ||
        !isModalOpen ||
        !selectedEvent ||
        !isConfirmedFutureSlot(selectedEvent) ||
        !selectedEvent.bookingId ||
        !selectedEvent.itemId,
    });

  const availableInspectorOptions = useMemo(() => {
    const list = (availableInspectors || []) as unknown as Array<
      Partial<Inspector> & {
        id?: string;
        name?: string | null;
        email?: string | null;
      }
    >;

    return list
      .filter((inspector) => !!inspector && !!inspector.id)
      .map((inspector) => {
        const value = inspector.id as string;
        const label =
          inspector.name || inspector.email || (inspector.id as string);
        return { value, label };
      });
  }, [availableInspectors]);

  // Clear any previous member selection on mount or club change to avoid persisting params after a full refresh
  useEffect(() => {
    setSelectedMemberId(null);
    setMemberManuallySelected(false);
  }, [clubId]);

  // If user loses permission to view club members, ensure filter is cleared
  useEffect(() => {
    if (canReadClubMembers) return;
    setSelectedMemberId(null);
    setMemberManuallySelected(false);
  }, [canReadClubMembers]);

  // Fetch members for member select dropdown
  const { data: membersRes } = useGetAllMembersQuery(
    {
      club_id: clubId || undefined,
      page: 1,
      pageSize: 100,
    },
    { skip: !clubId || !canReadClubMembers }
  ) as unknown as {
    data?: PaginatedApiResponse<MemberResponse>;
  };

  const memberOptions = useMemo(
    () =>
      (membersRes?.data || []).map((m) => ({
        value: m.user?.id,
        label: m.user?.name || m.user?.email || m.user?.id,
      })),
    [membersRes]
  );

  // Flatten booking items for Annual Inspections only for easy lookup
  const inspectionItems = useMemo(() => {
    const bookings = (bookingsRes?.data || []) as Array<{
      id: string;
      user: { name: string | null; email: string };
      items: Array<{
        id: string;
        resource: { id: string; name: string; resource_type: number };
        start_time: string;
        end_time: string;
        status: number;
        quantity: number | null;
        inspector?: { id: string; name: string | null; email: string } | null;
      }>;
    }>;

    return bookings.flatMap((b) =>
      b.items
        .filter(
          (it) => it.resource.resource_type === ResourceType.ANNUAL_INSPECTIONS
        )
        .map((it) => ({
          bookingId: b.id,
          user: b.user,
          ...it,
        }))
    );
  }, [bookingsRes]);

  const events: InspectorEvent[] = useMemo(() => {
    const assignments = (assignmentsRes?.data || []) as Array<{
      id: string;
      date: string; // YYYY-MM-DD
      start_time: string; // HH:mm
      end_time: string; // HH:mm
      resource: { id: string; name: string };
      inspector: { id: string; name: string | null; email: string };
      booked: boolean;
    }>;

    return assignments.map((slot) => {
      // Try to match a booking item to this slot
      const matched = inspectionItems.find((it) => {
        const sameDate = it.start_time.slice(0, 10) === slot.date;
        const sameResource = it.resource.id === slot.resource.id;
        const startHM = toUtcHM(it.start_time);
        const endHM = toUtcHM(it.end_time);
        const timeMatch =
          startHM === slot.start_time && endHM === slot.end_time;
        const inspectorOk = it.inspector?.id
          ? it.inspector.id === slot.inspector.id
          : true; // allow when inspector not attached to item

        // If it's cancelled, we only match if it was specifically assigned to this inspector
        if (it.status === ClubBookingStatus.CANCELLED) {
          return (
            sameDate &&
            sameResource &&
            timeMatch &&
            it.inspector?.id === slot.inspector.id
          );
        }

        return sameDate && sameResource && timeMatch && inspectorOk;
      });

      const status: CalendarEvent['status'] = matched
        ? matched.status === ClubBookingStatus.COMPLETED
          ? 'completed'
          : matched.status === ClubBookingStatus.CANCELLED
            ? 'cancelled'
            : 'confirmed'
        : slot.booked
          ? 'confirmed'
          : 'available';

      const title = matched
        ? `${slot.resource.name} - ${matched.user.name || matched.user.email}`
        : `${slot.resource.name} - ${slot.inspector.name || slot.inspector.email}`;

      const resourceObj = {
        id: slot.resource.id,
        name: slot.resource.name,
        description: 'Annual Inspection',
        price: 0,
        price_unit: PriceUnit.PER_DAY,
        quantity: matched?.quantity || 1,
        status: 1,
        resource_type: ResourceType.ANNUAL_INSPECTIONS,
      } as unknown as CalendarEvent['resource'];

      const ev: InspectorEvent = {
        id: `inspector-slot-${slot.id}${matched ? `-${matched.id}` : ''}`,
        title,
        start: slot.date,
        end: slot.date,
        status,
        berthNumber: '-',
        user: matched
          ? {
              name: matched.user.name || matched.user.email,
              email: matched.user.email,
            }
          : {
              name: slot.inspector.name || slot.inspector.email,
              email: slot.inspector.email,
            },
        assetType: 'resource' as AssetType,
        resource: resourceObj,
        inspector: slot.inspector,
        slotTime: { start: slot.start_time, end: slot.end_time },
        bookingId: matched?.bookingId,
        itemId: matched?.id,
        quantity: matched?.quantity || 1,
      };
      return ev;
    });
  }, [assignmentsRes, inspectionItems]);

  const filteredEvents = useMemo(() => {
    return events.filter((ev) => {
      if (!statusFilters.has(ev.status)) return false;
      if (
        userFilter &&
        !(
          ev.user?.name?.toLowerCase().includes(userFilter.toLowerCase()) ||
          ev.user?.email?.toLowerCase().includes(userFilter.toLowerCase())
        )
      )
        return false;
      if (selectedResource && ev.resource?.id !== selectedResource)
        return false;
      return true;
    });
  }, [events, statusFilters, userFilter, selectedResource]);

  const visibleRange = useMemo(() => {
    const base = toLocalMidnight(currentDate);

    if (view === 'day') {
      return { startDate: base, endDate: base };
    }

    if (view === 'week') {
      // Match CalendarGrid logic: start of week is Monday.
      const start = new Date(base);
      const dayOfWeek = (start.getDay() + 6) % 7;
      start.setDate(start.getDate() - dayOfWeek);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return { startDate: start, endDate: end };
    }

    // month
    const start = new Date(base.getFullYear(), base.getMonth(), 1);
    const end = new Date(base.getFullYear(), base.getMonth() + 1, 0);
    return { startDate: start, endDate: end };
  }, [currentDate, view]);

  const hasAnyEventsInVisibleRange = useMemo(() => {
    const startTs = visibleRange.startDate.getTime();
    const endTs = visibleRange.endDate.getTime();

    return filteredEvents.some((ev) => {
      const dt = parseYmdToLocal(ev.start);
      if (!dt) return false;
      const ts = toLocalMidnight(dt).getTime();
      return ts >= startTs && ts <= endTs;
    });
  }, [filteredEvents, visibleRange.endDate, visibleRange.startDate]);

  const showOutOfRangePanel =
    filteredEvents.length > 0 && !hasAnyEventsInVisibleRange;

  const activeStatusLabelForA11y = useMemo(() => {
    if (statusFilters.size !== 1) return null;
    const only = Array.from(statusFilters)[0];
    return only || null;
  }, [statusFilters]);

  const outOfRangeMonthGroups = useMemo(() => {
    if (!showOutOfRangePanel)
      return [] as Array<{
        year: number;
        month: number;
        label: string;
        count: number;
      }>;

    const map = new Map<
      string,
      { year: number; month: number; count: number }
    >();

    filteredEvents.forEach((ev) => {
      const dt = parseYmdToLocal(ev.start);
      if (!dt) return;
      const year = dt.getFullYear();
      const month = dt.getMonth();
      const key = `${year}-${month}`;
      const existing = map.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        map.set(key, { year, month, count: 1 });
      }
    });

    const groups = Array.from(map.values())
      .sort((a, b) => (a.year - b.year) * 12 + (a.month - b.month))
      .map((g) => {
        const label = formatDateWithOptions(
          new Date(g.year, g.month, 1),
          {
            month: 'long',
            year: 'numeric',
          },
          undefined
        );
        return { ...g, label };
      });

    return groups;
  }, [filteredEvents, showOutOfRangePanel]);

  const outOfRangeDateGroups = useMemo(() => {
    if (!showOutOfRangePanel)
      return [] as Array<{ date: string; label: string; count: number }>;

    const map = new Map<
      string,
      { date: string; label: string; count: number }
    >();

    filteredEvents.forEach((ev) => {
      const dt = parseYmdToLocal(ev.start);
      if (!dt) return;
      const date = ev.start;
      const existing = map.get(date);
      if (existing) {
        existing.count += 1;
        return;
      }

      const label = formatDateWithOptions(
        dt,
        {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        },
        undefined
      );

      map.set(date, { date, label, count: 1 });
    });

    return Array.from(map.values()).sort((a, b) =>
      a.date.localeCompare(b.date)
    );
  }, [filteredEvents, showOutOfRangePanel]);

  const handleGoToMonth = (year: number, month: number) => {
    setCurrentDate(new Date(year, month, 1));
  };

  const handleGoToDate = (ymd: string) => {
    const dt = parseYmdToLocal(ymd);
    if (!dt) return;
    setCurrentDate(dt);
  };

  const toggleStatusFilter = (status: string) => {
    setStatusFilters((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  const getStatusCount = (status: string, useFiltered = false) =>
    (useFiltered ? filteredEvents : events).filter((e) => e.status === status)
      .length;

  const resourceOptions = useMemo(() => {
    const map: Record<string, string> = {};
    events.forEach((e) => {
      if (e.resource?.id) map[e.resource.id] = e.resource.name || 'Resource';
    });
    return Object.entries(map).map(([value, label]) => ({ value, label }));
  }, [events]);

  const getResourceName = (resourceId: string | null) => {
    if (!resourceId) return 'Resource';
    const found = resourceOptions.find((o) => o.value === resourceId);
    return found?.label || 'Resource';
  };

  const handleEventClick = (event: InspectorEvent) => {
    setSelectedEvent(event);
    setSelectedInspectorUserId(event.inspector?.id || '');
    setIsModalOpen(true);
  };

  const handleReassignInspector = async () => {
    if (!clubId) return;
    if (!selectedEvent?.bookingId) return;
    if (!selectedEvent?.itemId) return;
    if (!selectedInspectorUserId) {
      enqueueSnackbar('Please select an inspector', { variant: 'error' });
      return;
    }

    try {
      await assignInspector({
        orderId: selectedEvent.bookingId,
        itemId: selectedEvent.itemId,
        inspector_id: selectedInspectorUserId,
      }).unwrap();

      enqueueSnackbar('Inspector reassigned successfully', {
        variant: 'success',
      });

      // refresh assignments for best UX (invalidatesTags will also refetch)
      const args: { club_id: string; member_id?: string } = { club_id: clubId };
      if (memberManuallySelected && selectedMemberId)
        args.member_id = selectedMemberId;
      triggerAssignments(args);

      setIsModalOpen(false);
    } catch {
      enqueueSnackbar('Failed to reassign inspector', { variant: 'error' });
    }
  };

  const handleMarkComplete = async () => {
    if (!selectedEvent?.itemId) return;
    try {
      setIsCompleting(true);
      await completeInspection({
        itemId: selectedEvent.itemId,
        quantity: selectedEvent.quantity || 1,
      }).unwrap();
      enqueueSnackbar('Inspection marked as complete', { variant: 'success' });
      setIsModalOpen(false);
    } catch {
      enqueueSnackbar('Failed to complete inspection', { variant: 'error' });
    } finally {
      setIsCompleting(false);
    }
  };

  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    if (view === 'month') newDate.setMonth(newDate.getMonth() - 1);
    else if (view === 'week') newDate.setDate(newDate.getDate() - 7);
    else newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (view === 'month') newDate.setMonth(newDate.getMonth() + 1);
    else if (view === 'week') newDate.setDate(newDate.getDate() + 7);
    else newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  const handleToday = () => setCurrentDate(new Date());

  const MAX_OUT_OF_RANGE_ITEMS = 10;

  if (!clubId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-lg font-medium text-gray-900 mb-2">
            No Club Selected
          </h2>
          <p className="text-gray-600">
            Please select a club to view inspector calendar.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden inspector-slot-calendar">
      <style>{`
        /* Hide the user text search input inside FilterSection for inspector view */
        .inspector-filter-wrapper .grid[class*="md:grid-cols-3"] > div:last-child { display: none; }

        /* Prevent CalendarView header from forcing horizontal scroll on small screens */
        @media (max-width: 640px) {
          .inspector-slot-calendar .calendar-view-header { flex-wrap: wrap; gap: 0.75rem; }
          .inspector-slot-calendar .calendar-toolbar { width: 100%; justify-content: flex-start; flex-wrap: wrap; }
          .inspector-slot-calendar .calendar-toolbar .view-switch { flex-wrap: wrap; }
          .inspector-slot-calendar .calendar-toolbar button span { display: none; }
        }

        /* Extra small screens: tighten paddings to avoid overflow below ~456px */
        @media (max-width: 456px) {
          .inspector-slot-calendar .calendar-view-header { gap: 0.5rem; }
          .inspector-slot-calendar .calendar-toolbar { gap: 0.5rem; }

          .inspector-slot-calendar .calendar-toolbar .view-switch button,
          .inspector-slot-calendar .calendar-view-header button {
            padding: 0.375rem 0.625rem;
            font-size: 0.75rem;
            line-height: 1rem;
          }

          .inspector-slot-calendar .calendar-view-header h1 {
            font-size: 1rem;
            line-height: 1.25rem;
            max-width: 100%;
            overflow-wrap: anywhere;
          }
        }
      `}</style>
      <div className="container mx-auto px-4 py-4 overflow-x-hidden">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4 max-w-full">
          <h1 className="text-2xl font-bold text-gray-900">
            Your Assigned Slots
          </h1>
          {canReadClubMembers && (
            <div className="flex items-center gap-2 w-full md:w-auto max-w-full">
              <div className="w-full md:w-auto max-w-full">
                <label
                  htmlFor="member-select"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Select Member
                </label>
                <select
                  id="member-select"
                  className="select select-sm min-w-36 h-8 sm:h-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={selectedMemberId || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedMemberId(val || null);
                    setMemberManuallySelected(!!val);
                  }}
                  autoComplete="off"
                >
                  <option value="">All Members</option>
                  {memberOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
        {/* Filters Section - reuse existing with a minimal subset; hide user text search via CSS */}
        <div className="inspector-filter-wrapper">
          <FilterSection
            statusFilters={statusFilters}
            toggleStatusFilter={toggleStatusFilter}
            assetTypeFilters={
              new Set<AssetType>(['resource']) as Set<AssetType>
            }
            toggleAssetTypeFilter={() => {}}
            userFilter={userFilter}
            setUserFilter={setUserFilter}
            selectedAssetType={'resource' as AssetType}
            setSelectedAssetType={() => {}}
            selectedBerth={selectedResource}
            setSelectedBerth={setSelectedResource}
            resetFilters={() => {
              setSelectedResource(null);
              setStatusFilters(
                new Set(['available', 'confirmed', 'completed', 'cancelled'])
              );
              setUserFilter('');
              setSelectedMemberId(null);
              setMemberManuallySelected(false);
            }}
            getStatusCount={getStatusCount}
            berthOptions={[]}
            addonOptions={resourceOptions}
            getAddonName={getResourceName}
            resourceOptions={resourceOptions}
            getResourceName={getResourceName}
          />
        </div>

        {(isAssignmentsLoading || isBookingsLoading) && (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading calendar...</span>
          </div>
        )}

        {/* Day events modal for +N more */}
        <DayEventsModal
          isOpen={dayEventsModal.open}
          date={dayEventsModal.date}
          events={dayEventsModal.events}
          onClose={() => setDayEventsModal({ open: false })}
          onEventClick={(ev) => {
            setSelectedEvent(ev as InspectorEvent);
            setIsModalOpen(true);
            setDayEventsModal({ open: false });
          }}
        />

        {/* Inspector action modal */}
        {isModalOpen && selectedEvent && (
          <div className="fixed inset-0 z-[999] p-2 sm:p-4 flex items-center justify-center">
            <button
              type="button"
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={() => setIsModalOpen(false)}
              aria-label="Close modal backdrop"
            />
            <dialog
              open
              ref={modalRef}
              className="bg-gray-100 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] sm:max-h-[500px] overflow-y-auto custom-scrollbar relative"
              aria-labelledby="inspector-modal-title"
            >
              <div className="p-4 sm:p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3
                      id="inspector-modal-title"
                      className="text-lg font-semibold text-gray-900"
                    >
                      Inspector Slot Details
                    </h3>
                    <div className="mt-1">
                      <span className="inline-block px-1.5 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                        Annual Inspection
                      </span>
                    </div>
                  </div>
                  <button
                    ref={closeButtonRef}
                    onClick={() => setIsModalOpen(false)}
                    className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label="Close modal"
                  >
                    ×
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="border rounded-lg p-3 bg-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      {(() => {
                        const config = statusConfig[selectedEvent.status];
                        const colorClass = config?.color || 'bg-green-500';
                        return (
                          <span
                            className={`w-3 h-3 rounded-full ${colorClass}`}
                          ></span>
                        );
                      })()}
                      <span className="text-sm font-medium text-gray-700 capitalize">
                        {selectedEvent.status}
                      </span>
                    </div>
                    <h4 className="font-medium text-gray-900">
                      {selectedEvent.title}
                    </h4>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Resource</span>
                      <p className="font-medium">
                        {selectedEvent.resource?.name}
                      </p>
                    </div>
                    <div>
                      <div className="text-gray-500">Inspector</div>
                      <p
                        className="font-medium truncate max-w-full overflow-hidden"
                        title={
                          selectedEvent.inspector?.email ||
                          selectedEvent.inspector?.name ||
                          ''
                        }
                      >
                        {selectedEvent.inspector?.name ||
                          selectedEvent.inspector?.email}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Date</span>
                      <p className="font-medium">
                        {formatDisplayDate(new Date(selectedEvent.start))}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Time</span>
                      <p className="font-medium">
                        {selectedEvent.slotTime?.start} -{' '}
                        {selectedEvent.slotTime?.end}
                      </p>
                    </div>
                    {selectedEvent.bookingId && (
                      <div className="col-span-2">
                        <span className="text-gray-500">Boater</span>
                        <p className="font-medium">
                          {selectedEvent.user?.name} (
                          {selectedEvent.user?.email})
                        </p>
                      </div>
                    )}

                    {isConfirmedFutureSlot(selectedEvent) &&
                      !!selectedEvent.bookingId &&
                      !!selectedEvent.itemId &&
                      (canReassignInspector ? (
                        <div className="col-span-2">
                          <label
                            htmlFor="reassign-inspector"
                            className="text-gray-500"
                          >
                            Change / Reassign Inspector
                          </label>
                          <select
                            id="reassign-inspector"
                            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-gray-50 contrast:bg-black contrast:text-white"
                            value={selectedInspectorUserId}
                            onChange={(e) =>
                              setSelectedInspectorUserId(e.target.value)
                            }
                            autoComplete="off"
                          >
                            <option value="">Select inspector</option>
                            {availableInspectorOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          {isFetchingInspectors && (
                            <div className="mt-1 text-xs text-gray-600">
                              Loading available inspectors...
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="col-span-2">
                          <span className="text-gray-500">Inspector</span>
                          <p
                            className="font-medium truncate max-w-full overflow-hidden"
                            title={
                              selectedEvent.inspector?.email ||
                              selectedEvent.inspector?.name ||
                              ''
                            }
                          >
                            {selectedEvent.inspector?.name ||
                              selectedEvent.inspector?.email ||
                              '—'}
                          </p>
                        </div>
                      ))}
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg bg-gray-200 hover:bg-gray-300 transition-colors"
                  >
                    Close
                  </button>

                  {selectedEvent.status !== 'cancelled' &&
                    isConfirmedFutureSlot(selectedEvent) &&
                    !!selectedEvent.bookingId &&
                    !!selectedEvent.itemId &&
                    canReassignInspector && (
                      <button
                        onClick={handleReassignInspector}
                        className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                        disabled={
                          isAssigningInspector ||
                          !selectedInspectorUserId ||
                          selectedInspectorUserId ===
                            (selectedEvent.inspector?.id || '')
                        }
                      >
                        {isAssigningInspector
                          ? 'Updating...'
                          : 'Change Inspector'}
                      </button>
                    )}
                  <button
                    onClick={() =>
                      selectedEvent.bookingId &&
                      navigate(
                        `/club-booking-management/${selectedEvent.bookingId}`
                      )
                    }
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    disabled={!selectedEvent.bookingId}
                  >
                    View
                  </button>
                  {selectedEvent.status !== 'cancelled' && (
                    <button
                      onClick={handleMarkComplete}
                      className="flex-1 px-4 py-2 bg-green-600 dark:bg-green-700 contrast:bg-green-700 text-white dark:text-white contrast:text-white rounded-lg hover:bg-green-700 dark:hover:bg-green-800 contrast:hover:bg-green-800 transition-colors disabled:opacity-50"
                      disabled={
                        !selectedEvent.itemId ||
                        selectedEvent.status === 'completed' ||
                        isCompleting
                      }
                    >
                      {isCompleting ? 'Completing...' : 'Mark as Complete'}
                    </button>
                  )}
                </div>
              </div>
            </dialog>
          </div>
        )}

        {filteredEvents.length > 0 && (
          <>
            {showOutOfRangePanel && (
              <div className="mb-4 rounded-xl border border-gray-300 bg-gray-100 dark:bg-gray-900/30 px-4 py-3">
                <div aria-live="polite" className="sr-only">
                  {`${filteredEvents.length} ${
                    activeStatusLabelForA11y
                      ? `${activeStatusLabelForA11y} inspection`
                      : 'inspection'
                  }${filteredEvents.length === 1 ? '' : 's'} found outside the current ${
                    view
                  }`}
                </div>

                <div className="flex flex-col gap-2">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {`${filteredEvents.length} inspection${
                      filteredEvents.length === 1 ? '' : 's'
                    } match your filters outside the current ${view}`}
                  </div>

                  {view === 'month' ? (
                    outOfRangeMonthGroups.length === 1 ? (
                      <button
                        type="button"
                        onClick={() =>
                          handleGoToMonth(
                            outOfRangeMonthGroups[0].year,
                            outOfRangeMonthGroups[0].month
                          )
                        }
                        className="self-start px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 bg-blue-600 text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100 dark:focus:ring-offset-gray-900"
                        aria-label={`Go to ${outOfRangeMonthGroups[0].label}, ${
                          outOfRangeMonthGroups[0].count
                        } ${
                          activeStatusLabelForA11y
                            ? `${activeStatusLabelForA11y} inspection`
                            : 'inspection'
                        }${
                          outOfRangeMonthGroups[0].count === 1 ? '' : 's'
                        } matching your filters`}
                      >
                        {`Go to ${outOfRangeMonthGroups[0].label}`}
                      </button>
                    ) : (
                      <div className="flex flex-col gap-2">
                        <div className="flex flex-row flex-wrap gap-2 overflow-x-auto">
                          {(showAllOutOfRangeMonths
                            ? outOfRangeMonthGroups
                            : outOfRangeMonthGroups.slice(
                                0,
                                MAX_OUT_OF_RANGE_ITEMS
                              )
                          ).map((g) => (
                            <button
                              key={`${g.year}-${g.month}`}
                              type="button"
                              onClick={() => handleGoToMonth(g.year, g.month)}
                              className="w-fit whitespace-nowrap text-left px-3 py-2 rounded-lg border border-gray-300 bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100 dark:focus:ring-offset-gray-900"
                              aria-label={`Go to ${g.label}, ${g.count} ${
                                activeStatusLabelForA11y
                                  ? `${activeStatusLabelForA11y} inspection`
                                  : 'inspection'
                              }${g.count === 1 ? '' : 's'} matching your filters`}
                            >
                              <span className="text-sm font-medium">
                                {g.label}
                              </span>{' '}
                              <span className="text-sm text-gray-700 dark:text-gray-200">
                                ({g.count})
                              </span>
                            </button>
                          ))}
                        </div>

                        {outOfRangeMonthGroups.length >
                          MAX_OUT_OF_RANGE_ITEMS && (
                          <button
                            type="button"
                            onClick={() =>
                              setShowAllOutOfRangeMonths((prev) => !prev)
                            }
                            className="self-start text-sm font-medium text-blue-700 dark:text-blue-300 hover:underline contrast:text-blue-300"
                          >
                            {showAllOutOfRangeMonths
                              ? 'Show less'
                              : `Show all (${outOfRangeMonthGroups.length})`}
                          </button>
                        )}
                      </div>
                    )
                  ) : outOfRangeDateGroups.length === 1 ? (
                    <button
                      type="button"
                      onClick={() =>
                        handleGoToDate(outOfRangeDateGroups[0].date)
                      }
                      className="self-start px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 bg-blue-600 text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100 dark:focus:ring-offset-gray-900"
                      aria-label={`Go to ${outOfRangeDateGroups[0].label}, ${
                        outOfRangeDateGroups[0].count
                      } ${
                        activeStatusLabelForA11y
                          ? `${activeStatusLabelForA11y} inspection`
                          : 'inspection'
                      }${
                        outOfRangeDateGroups[0].count === 1 ? '' : 's'
                      } matching your filters`}
                    >
                      {`Go to ${outOfRangeDateGroups[0].label}`}
                    </button>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-row flex-wrap gap-2 overflow-x-auto">
                        {(showAllOutOfRangeDates
                          ? outOfRangeDateGroups
                          : outOfRangeDateGroups.slice(
                              0,
                              MAX_OUT_OF_RANGE_ITEMS
                            )
                        ).map((g) => (
                          <button
                            key={g.date}
                            type="button"
                            onClick={() => handleGoToDate(g.date)}
                            className="w-fit whitespace-nowrap text-left px-3 py-2 rounded-lg border border-gray-300 bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100 dark:focus:ring-offset-gray-900"
                            aria-label={`Go to ${g.label}, ${g.count} ${
                              activeStatusLabelForA11y
                                ? `${activeStatusLabelForA11y} inspection`
                                : 'inspection'
                            }${g.count === 1 ? '' : 's'} matching your filters`}
                          >
                            <span className="text-sm font-medium">
                              {g.label}
                            </span>{' '}
                            <span className="text-sm text-gray-700 dark:text-gray-200">
                              ({g.count})
                            </span>
                          </button>
                        ))}
                      </div>

                      {outOfRangeDateGroups.length > MAX_OUT_OF_RANGE_ITEMS && (
                        <button
                          type="button"
                          onClick={() =>
                            setShowAllOutOfRangeDates((prev) => !prev)
                          }
                          className="self-start text-sm font-medium text-blue-700 dark:text-blue-300 hover:underline contrast:text-blue-300"
                        >
                          {showAllOutOfRangeDates
                            ? 'Show less'
                            : `Show all (${outOfRangeDateGroups.length})`}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            <CalendarView
              currentDate={currentDate}
              view={view}
              setView={setView}
              filteredEvents={filteredEvents}
              handlePrevious={handlePrevious}
              handleNext={handleNext}
              handleToday={handleToday}
              handleEventClick={handleEventClick}
              onMoreClick={(date, events) =>
                setDayEventsModal({ open: true, date, events })
              }
            />
          </>
        )}

        {filteredEvents.length === 0 &&
          !isAssignmentsLoading &&
          !isBookingsLoading && (
            <div className="text-center py-12">
              <h2 className="text-lg font-medium text-gray-900 mb-2">
                No inspector slots
              </h2>
              <p className="text-gray-600">
                No slots match your current filters.
              </p>
              <button
                onClick={() => {
                  setSelectedResource(null);
                  setStatusFilters(
                    new Set(['available', 'confirmed', 'completed'])
                  );
                  setUserFilter('');
                  setSelectedMemberId(null);
                  setMemberManuallySelected(false);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Reset Filters
              </button>
            </div>
          )}
      </div>
    </div>
  );
}
